/**
 * Third-party vendor credentials (music identification today).
 *
 * The backend describes each provider as a LIST of self-describing fields
 * rather than a `{name: value}` map, and expects the same shape back. That
 * is deliberate: `json_response` camel-cases every object KEY it walks, so
 * a map keyed by field name would reach us as `accessKey` while the stored
 * SSM parameter — and the payload the backend parses — still says
 * `access_key`. As list entries the names are values, and round-trip
 * untouched. (Same trap as the gateway catalog payloads, see payloadCase.ts.)
 *
 * Secrets are write-only: a response never carries one, only `configured`
 * and the date it was last set.
 */

import { apiCall } from "@/api/client";

export interface IntegrationField {
  name: string;
  label: string;
  secret: boolean;
  multiline: boolean;
  /** Public fields only. */
  value?: string;
  /** Secret fields only — whether one is stored. */
  configured?: boolean;
  /** Secret fields only — when it was last written (ms epoch). */
  updatedAt?: number | null;
}

/** Where the effective configuration comes from. */
export type IntegrationSource = "global" | "stage" | "unset";

/** Comment le fournisseur nous est facturé. */
export type IntegrationBilling = "free" | "metered" | "flat_rate";

export interface Integration {
  provider: string;
  /**
   * Le nom COMMERCIAL du service — « ACRCloud », « Spotify ».
   *
   * Il descend du backend, et de lui seul. Il a longtemps vécu en dur dans
   * trois écrans qui ne disaient pas la même chose, et deux cartes sur quatre
   * ne nommaient leur fournisseur nulle part.
   */
  vendor: string;
  /** Ce que le fournisseur fait pour nous — sert de titre de carte. */
  label: string;
  /** Ce qu'il apporte, et ce qu'il n'apporte pas. */
  purpose: string;
  billing: IntegrationBilling;
  fields: IntegrationField[];
  updatedAt?: number | null;
  source: IntegrationSource;
  /** False when the provider has no connectivity probe. */
  testable: boolean;
  /** Se configure une seule fois, pour tous les clients. */
  globalOnly: boolean;
  /** Peut être éteint depuis ici — vrai des seules sources que le control
   *  plane consomme lui-même. */
  switchable: boolean;
  enabled: boolean;
}

export interface IntegrationTestResult {
  ok: boolean;
  /** True when the vendor rejected the credentials, as opposed to being
   *  unreachable — a typo and an outage read very differently. */
  rejected: boolean;
  message: string;
  source: IntegrationSource;
}

/** Only the fields the operator actually edited need to be sent: an
 *  omitted field keeps its stored value. */
export interface IntegrationUpdate {
  fields: { name: string; value: string }[];
}

/**
 * Le catalogue des fournisseurs, dans l'ordre du registre backend.
 *
 * C'est lui qui permet de DÉCOUVRIR les fournisseurs. Sans cette route, la
 * page en tenait une liste en dur, forcément en retard sur le backend — et
 * ajouter un fournisseur demandait de toucher au frontend.
 */
export function listIntegrations(token: string) {
  return apiCall<{ items: Integration[] }>("/settings/integrations", { token });
}

/**
 * Allume ou éteint une source que le control plane interroge lui-même.
 *
 * Séparé du PUT des identifiants à dessein : couper une source facturée ne
 * doit jamais passer par un écrasement de secrets, et les détruire pour
 * désactiver serait un aller simple — on les repaierait au rallumage.
 */
export function setIntegrationEnabled(
  token: string,
  provider: string,
  enabled: boolean,
) {
  return apiCall<Integration>(
    `/settings/integrations/${encodeURIComponent(provider)}/enabled`,
    { method: "PUT", body: { enabled }, token },
  );
}

export function getGlobalIntegration(token: string, provider: string) {
  return apiCall<Integration>(
    `/settings/integrations/${encodeURIComponent(provider)}`,
    { token },
  );
}

export function putGlobalIntegration(
  token: string,
  provider: string,
  input: IntegrationUpdate,
) {
  return apiCall<Integration>(
    `/settings/integrations/${encodeURIComponent(provider)}`,
    { method: "PUT", body: input, token },
  );
}

export function getStageIntegration(
  token: string,
  tenantId: string,
  stage: string,
  provider: string,
) {
  return apiCall<Integration>(
    `/tenants/${encodeURIComponent(tenantId)}/stages/${encodeURIComponent(stage)}` +
      `/integrations/${encodeURIComponent(provider)}`,
    { token },
  );
}

export function putStageIntegration(
  token: string,
  tenantId: string,
  stage: string,
  provider: string,
  input: IntegrationUpdate,
) {
  return apiCall<Integration>(
    `/tenants/${encodeURIComponent(tenantId)}/stages/${encodeURIComponent(stage)}` +
      `/integrations/${encodeURIComponent(provider)}`,
    { method: "PUT", body: input, token },
  );
}

/**
 * Validate what is STORED against the vendor — not what the form holds.
 * Values only reach the tenant account at deploy time, so without this a
 * typo would surface a deploy and an upload later.
 */
export function testIntegration(
  token: string,
  provider: string,
  scope?: { tenantId: string; stage: string },
) {
  return apiCall<IntegrationTestResult>(
    `/settings/integrations/${encodeURIComponent(provider)}/test`,
    {
      method: "POST",
      body: scope ? { tenant_id: scope.tenantId, stage: scope.stage } : {},
      token,
    },
  );
}
